import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { Page } from "../../../components/page-base";
import { Container } from "../../sellers/pages/styles";
import useUserStore from "../../../store/user";
import { FollowingProfilesService } from "../../following-profiles/services";
import { handleError } from "../../../utils/parseApiReturn";
import { Botao, Hint } from "../../profiles-tracking/components/edit-profile-modal/components/EditorChrome";
import {
  ativarNotificacoes,
  enviarAoPerfil,
  enviarTesteDaMensagem,
  situacaoAtual,
  situacaoDoPerfil,
  type HistoricoDeEnvio,
  type SituacaoDasNotificacoes,
  type SituacaoDoPerfil,
} from "../../../push";
import { useRelatorio } from "../../relatorio/hooks/use-relatorio";
import {
  CATEGORIAS,
  LIMITE_TEXTO,
  LIMITE_TITULO,
  MODELOS,
  SECOES,
  dadosDoCliente,
  linkDaSecao,
  situacaoDoTamanho,
  variaveisParaInserir,
  type Categoria,
  type Modelo,
} from "../dados";
import { PreviaDaNotificacao } from "../previa";

type Opcao = { publicId: string; nome: string };

// Largura flexível com teto: largura fixa passava do container (centralizado) e ia para baixo do menu lateral.
const LARGURA = { width: "100%", maxWidth: 1180, px: { xs: 2, md: 4 }, boxSizing: "border-box" } as const;
const COR_DO_TAMANHO = { ok: "#137333", longo: "#A85F00", excede: "#B3261E" } as const;

const contador = (texto: string, limite: { ideal: number; maximo: number }) => {
  const situacao = situacaoDoTamanho(texto, limite);
  const aviso =
    situacao === "excede" ? " · passou do máximo" : situacao === "longo" ? ` · acima de ${limite.ideal} pode ser cortado no celular` : "";
  return <span style={{ color: COR_DO_TAMANHO[situacao] }}>{`${texto.length}/${limite.ideal}${aviso}`}</span>;
};

const rotuloDoTipo = (h: HistoricoDeEnvio) =>
  h.tipo === "RESUMO_SEMANAL" ? "recibo semanal automático" : h.tipo === "RESUMO_DIARIO" ? "resumo diário automático" : "enviada pela equipe";

/** Uma linha do funil: aceito pelo provedor não é "visto"; clique e abertura são a prova. */
const Funil = ({ h }: { h: HistoricoDeEnvio }) => {
  const etapas: [string, number | undefined][] = [
    ["aparelhos", h.aparelhos],
    ["aceitos", h.entregues],
    ["chegaram", h.recebidos],
    ["tocaram", h.cliques],
    ["abriram", h.aberturas],
  ];
  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.5 }}>
      {etapas.map(([rotulo, valor]) => (
        <Typography key={rotulo} component="span" sx={{ fontSize: 12, color: "#5F6368" }}>
          <b style={{ color: "#202124" }}>{valor ?? "—"}</b> {rotulo}
        </Typography>
      ))}
    </Box>
  );
};

/** Modelos e editor de um perfil: os textos saem com os números do relatório desse cliente. */
const EditorDoPerfil = ({ perfil, situacao, aoEnviar }: { perfil: Opcao; situacao: SituacaoDoPerfil | null; aoEnviar: () => void }) => {
  const relatorio = useRelatorio(perfil.publicId, 30, { historico: true, concorrentes: true });
  const dados = useMemo(
    () =>
      relatorio.perfil
        ? dadosDoCliente(relatorio.perfil, { historico: relatorio.historico, concorrentes: relatorio.concorrentes, pesquisas: relatorio.pesquisas })
        : null,
    [relatorio.perfil, relatorio.historico, relatorio.concorrentes, relatorio.pesquisas],
  );
  const [categoria, setCategoria] = useState<Categoria | "TODAS">("TODAS");
  const [modelo, setModelo] = useState<Modelo | null>(null);
  const [titulo, setTitulo] = useState("");
  const [texto, setTexto] = useState("");
  const [secao, setSecao] = useState("resultados");
  const [acimaDoTetoOk, setAcimaDoTetoOk] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [meuAparelho, setMeuAparelho] = useState<SituacaoDasNotificacoes | null>(null);

  useEffect(() => {
    situacaoAtual().then(setMeuAparelho).catch(() => setMeuAparelho("sem-suporte"));
  }, []);

  const aplicar = (escolhido: Modelo) => {
    if (!dados) return;
    setModelo(escolhido);
    setTitulo(escolhido.titulo(dados));
    setTexto(escolhido.texto(dados));
    setSecao(escolhido.secao);
  };

  const inserir = (valor: string) => setTexto((atual) => (atual ? `${atual.trimEnd()} ${valor}` : valor));

  const dia = situacao?.ultimas24h ?? 0;
  const semana = situacao?.ultimos7dias ?? 0;
  const tetoDia = situacao?.tetoDia ?? 1;
  const tetoSemana = situacao?.tetoSemana ?? 3;
  const acimaDoTeto = dia >= tetoDia || semana >= tetoSemana;
  const tamanhoOk =
    titulo.trim().length > 0 && texto.trim().length > 0 && titulo.length <= LIMITE_TITULO.maximo && texto.length <= LIMITE_TEXTO.maximo;
  const mensagem = { title: titulo.trim(), body: texto.trim(), url: linkDaSecao(perfil.publicId, secao), category: modelo?.categoria };

  const enviar = async () => {
    setEnviando(true);
    try {
      const r = await enviarAoPerfil(perfil.publicId, { ...mensagem, force: acimaDoTeto && acimaDoTetoOk });
      if (r.aparelhos === 0) {
        toast.info("Registrado, mas nenhum cliente deste perfil ativou as notificações ainda.");
      } else {
        toast.success(`Aceita pelo celular em ${r.entregues} de ${r.aparelhos} aparelho(s).`);
      }
      setModelo(null);
      setTitulo("");
      setTexto("");
      setAcimaDoTetoOk(false);
      aoEnviar();
    } catch (erro) {
      toast.error(handleError(erro, "Não foi possível enviar a notificação.").message);
    } finally {
      setEnviando(false);
    }
  };

  const testar = async () => {
    try {
      if (meuAparelho !== "ativadas") {
        const nova = await ativarNotificacoes();
        setMeuAparelho(nova);
        if (nova !== "ativadas") {
          toast.info("Libere as notificações deste navegador para receber o teste.");
          return;
        }
      }
      const enviados = await enviarTesteDaMensagem(mensagem);
      toast.info(enviados > 0 ? "Teste enviado para os seus aparelhos." : "Nenhum aparelho seu recebeu. Ative de novo.");
    } catch (erro) {
      toast.error(handleError(erro, "Não foi possível enviar o teste.").message);
    }
  };

  const modelosVisiveis = MODELOS.filter((m) => categoria === "TODAS" || m.categoria === categoria);
  const clientesComApp = situacao?.clientesComAparelho ?? 0;

  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 340px" }, alignItems: "start" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>1. Escolha o que comprovar</Typography>
          <Hint>Só ficam liberados os modelos que os dados deste cliente sustentam. O texto já vem com os números do relatório dele.</Hint>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1.5 }} role="tablist" aria-label="Tipo de notificação">
            {[{ id: "TODAS" as const, rotulo: "Todos", icone: "apps" }, ...CATEGORIAS].map((c) => (
              <Botao
                key={c.id}
                type="button"
                role="tab"
                aria-selected={categoria === c.id}
                $variante={categoria === c.id ? "filled" : "tonal"}
                style={{ height: 32, fontSize: 13 }}
                onClick={() => setCategoria(c.id)}
              >
                <span className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>{c.icone}</span>
                {c.rotulo}
              </Botao>
            ))}
          </Box>
          {relatorio.carregando || !dados ? (
            <Hint>{relatorio.falhou ? "Não foi possível carregar os dados deste perfil." : "Carregando os dados do relatório..."}</Hint>
          ) : (
            <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
              {modelosVisiveis.map((m) => {
                const falta = m.falta(dados);
                const ativo = modelo?.id === m.id;
                const cat = CATEGORIAS.find((c) => c.id === m.categoria);
                return (
                  <Box
                    key={m.id}
                    component="button"
                    type="button"
                    disabled={!!falta}
                    onClick={() => aplicar(m)}
                    aria-pressed={ativo}
                    sx={{
                      textAlign: "left",
                      p: 1.5,
                      borderRadius: 2.5,
                      border: `1px solid ${ativo ? "#0B57D0" : "#E8EAED"}`,
                      background: ativo ? "#E8F0FE" : falta ? "#F8F9FA" : "#fff",
                      cursor: falta ? "not-allowed" : "pointer",
                      opacity: falta ? 0.6 : 1,
                      font: "inherit",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      "&:hover": falta ? undefined : { borderColor: "#0B57D0" },
                    }}
                  >
                    <Typography component="span" sx={{ fontSize: 11, color: "#5F6368", display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span className="material-icons" aria-hidden="true" style={{ fontSize: 14 }}>{cat?.icone}</span>
                      {cat?.rotulo}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: "#202124" }}>{m.titulo(dados)}</Typography>
                    <Typography component="span" sx={{ fontSize: 13, color: falta ? "#80868B" : "#3C4043" }}>
                      {falta ?? m.texto(dados)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>2. Ajuste a mensagem</Typography>
          <TextField
            label="Título"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            inputProps={{ maxLength: LIMITE_TITULO.maximo }}
            helperText={contador(titulo, LIMITE_TITULO)}
          />
          <TextField
            label="Mensagem"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            inputProps={{ maxLength: LIMITE_TEXTO.maximo }}
            helperText={contador(texto, LIMITE_TEXTO)}
            multiline
            minRows={3}
          />
          {dados && (
            <Box>
              <Hint>Inserir um dado real deste cliente:</Hint>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
                {variaveisParaInserir(dados).map((v) => (
                  <Botao key={v.rotulo} type="button" $variante="outlined" style={{ height: 28, fontSize: 12 }} onClick={() => inserir(v.valor)} title={v.valor}>
                    {`${v.rotulo}: ${v.valor.length > 28 ? `${v.valor.slice(0, 27)}…` : v.valor}`}
                  </Botao>
                ))}
              </Box>
            </Box>
          )}
          <TextField select label="Ao tocar, abre" value={secao} onChange={(e) => setSecao(e.target.value)} helperText="O cliente cai direto nesta parte do relatório dele.">
            {SECOES.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.rotulo}</MenuItem>
            ))}
          </TextField>
        </Paper>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, position: { md: "sticky" }, top: { md: 16 } }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1.5 }}>3. Confira e envie</Typography>
          <PreviaDaNotificacao titulo={titulo} texto={texto} />
          <Hint style={{ display: "block", marginTop: 8 }}>A aparência final depende do sistema e das configurações do aparelho.</Hint>

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, background: acimaDoTeto ? "#FEF7E0" : "#F8F9FA" }} role="status">
            <Typography sx={{ fontSize: 13, color: "#202124" }}>
              {`Este perfil recebeu ${dia} nas últimas 24 h e ${semana} nos últimos 7 dias.`}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#5F6368" }}>
              {`Teto para não virar spam: ${tetoDia} por dia e ${tetoSemana} por semana.`}
            </Typography>
            {acimaDoTeto && (
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={<Checkbox size="small" checked={acimaDoTetoOk} onChange={(e) => setAcimaDoTetoOk(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 13 }}>É importante: enviar mesmo acima do teto</Typography>}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
            <Botao
              type="button"
              $variante="filled"
              onClick={enviar}
              disabled={enviando || !tamanhoOk || !situacao?.ligado || (acimaDoTeto && !acimaDoTetoOk)}
            >
              <span className="material-icons" aria-hidden="true">send</span>
              {enviando ? "Enviando..." : clientesComApp > 0 ? `Enviar para ${clientesComApp} cliente(s)` : "Enviar (ninguém com o app ainda)"}
            </Botao>
            <Botao type="button" $variante="outlined" onClick={testar} disabled={!tamanhoOk || meuAparelho === "sem-suporte" || meuAparelho === "bloqueadas"}>
              <span className="material-icons" aria-hidden="true">phone_iphone</span>
              {meuAparelho === "ativadas" ? "Enviar teste para mim" : "Ativar e testar neste aparelho"}
            </Botao>
            <Hint>Só fale do que foi feito ou medido de verdade. Nada de nome de quem avaliou na mensagem.</Hint>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

/** Equipe: avisar no celular os clientes de um perfil, com prova de entrega e de resultado. */
export const Notificacoes: React.FC = () => {
  const { user } = useUserStore();
  const [busca, setBusca] = useState("");
  const [opcoes, setOpcoes] = useState<Opcao[]>([]);
  const [perfil, setPerfil] = useState<Opcao | null>(null);
  const [situacao, setSituacao] = useState<SituacaoDoPerfil | null>(null);

  useEffect(() => {
    if (!user?.token) return;
    let vivo = true;
    const espera = window.setTimeout(async () => {
      const dados = await FollowingProfilesService.getFollowingProfiles({ token: user.token, page: 0, size: 20, query: busca });
      if (!vivo || !dados?.content) return;
      setOpcoes(
        dados.content.map((p: { profileBusinessId: string; razaoSocial?: string; name?: string }) => ({
          publicId: p.profileBusinessId,
          nome: p.razaoSocial || p.name || p.profileBusinessId,
        })),
      );
    }, 350);
    return () => {
      vivo = false;
      window.clearTimeout(espera);
    };
  }, [busca, user?.token]);

  const carregarSituacao = async (escolhido: Opcao | null) => {
    if (!escolhido) {
      setSituacao(null);
      return;
    }
    try {
      setSituacao(await situacaoDoPerfil(escolhido.publicId));
    } catch (erro) {
      toast.error(handleError(erro, "Não foi possível carregar o perfil.").message);
    }
  };

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Enviar push"
          subtitle="Notificação no celular do cliente que comprova o trabalho e o resultado. Toda sexta às 16:30 o sistema manda sozinho o recibo da semana."
        />

        <Box sx={{ ...LARGURA, mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Autocomplete
              options={opcoes}
              value={perfil}
              getOptionLabel={(o) => o.nome}
              isOptionEqualToValue={(a, b) => a.publicId === b.publicId}
              filterOptions={(x) => x}
              onInputChange={(_, valor) => setBusca(valor)}
              onChange={(_, escolhido) => {
                setPerfil(escolhido);
                setSituacao(null);
                carregarSituacao(escolhido);
              }}
              noOptionsText="Nenhum perfil encontrado"
              renderInput={(params) => <TextField {...params} label="Cliente (perfil)" placeholder="Buscar pelo nome" />}
            />
            {situacao && (
              <Typography sx={{ mt: 1.5, fontSize: 14 }} role="status">
                {situacao.clientes === 0
                  ? "Este perfil não tem cliente com acesso. Libere em Equipe e acessos."
                  : `${situacao.clientes} cliente(s) com acesso · ${situacao.clientesComAparelho} com o app e notificação ativada · ${situacao.aparelhos} aparelho(s)`}
                {!situacao.ligado && " · as notificações estão desligadas no servidor"}
              </Typography>
            )}
          </Paper>

          {perfil && <EditorDoPerfil key={perfil.publicId} perfil={perfil} situacao={situacao} aoEnviar={() => carregarSituacao(perfil)} />}

          {situacao && situacao.historico.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 600 }}>Comprovação dos envios</Typography>
              <Hint>
                Aceito = o serviço do celular recebeu. Chegou = apareceu no aparelho. Tocou e abriu = o cliente viu a prova. Envios antes de 05/10 só têm aceitos.
              </Hint>
              {situacao.historico.map((h) => (
                <Box key={h.enviadaEm + h.titulo} sx={{ py: 1.25, borderTop: "1px solid #EEF1F4", mt: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {h.titulo}
                    <Typography component="span" sx={{ fontSize: 12, color: "#5F6368", ml: 1 }}>
                      {new Date(h.enviadaEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      {` · ${rotuloDoTipo(h)}`}
                      {h.categoria ? ` · ${CATEGORIAS.find((c) => c.id === h.categoria)?.rotulo ?? h.categoria}` : ""}
                    </Typography>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#3C4043" }}>{h.texto}</Typography>
                  <Funil h={h} />
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      </Container>
    </Page.Root>
  );
};

export default Notificacoes;
